import { useEffect, useState } from "react";
import { track } from "../features/analytics/tracker";
import { useDeliveryStatus, formatResume } from "./deliveryStatus";

// Live "order by" countdown. Mirrors the published delivery rhythm:
//   order before 10:00am → morning round (10:30am–1:00pm)
//   order before 4:00pm  → evening round (5:30pm–7:00pm)
//   later               → next morning's round
function nextSlot(now = new Date()) {
  const hour = now.getHours() + now.getMinutes() / 60;

  const at = (h, m = 0, dayOffset = 0) => {
    const d = new Date(now);
    d.setDate(d.getDate() + dayOffset);
    d.setHours(h, m, 0, 0);
    return d;
  };

  if (hour < 10) {
    return { cutoff: at(10), slotLabel: "today, 10:30am–1:00pm", slot: "morning" };
  }
  if (hour < 16) {
    return { cutoff: at(16), slotLabel: "this evening, 5:30–7:00pm", slot: "evening" };
  }
  return { cutoff: at(10, 0, 1), slotLabel: "tomorrow morning, 10:30am–1:00pm", slot: "next_morning" };
}

function formatLeft(ms) {
  const mins = Math.max(1, Math.ceil(ms / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 2) return `${h}h ${m}m`;
  if (h >= 1) return `${h}h ${m}m`;
  return `${m} min`;
}

// nextSlot()'s slot values map onto the server's three window keys.
const SLOT_TO_WINDOW = { morning: "morning", evening: "evening", next_morning: "morning" };

export default function DeliveryCountdown({ placement = "home", style = {} }) {
  const [now, setNow] = useState(() => new Date());
  const { status } = useDeliveryStatus();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    track("delivery_countdown_view", { placement });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { cutoff, slotLabel, slot } = nextSlot(now);
  const left = formatLeft(cutoff.getTime() - now.getTime());

  const windowKey = SLOT_TO_WINDOW[slot];
  const win = windowKey ? status?.windows?.[windowKey] : null;

  // A delivery pause never stops an order. If this round is affected, say when
  // it will actually arrive rather than counting down to a slot we'll miss.
  if (win?.deferred) {
    const resumes = formatResume(win.delivery || status?.nextAvailable?.date);
    return (
      <div className="delivery-countdown" style={style} role="status">
        <span className="delivery-countdown__dot" aria-hidden="true" />
        <span>
          Deliveries are temporarily paused
          {resumes ? <>, and resume on <strong>{resumes}</strong></> : null}. Order now and yours goes out first.
        </span>
      </div>
    );
  }

  return (
    <div className="delivery-countdown" style={style} role="status">
      <span className="delivery-countdown__dot" aria-hidden="true" />
      <span>
        Order within <strong>{left}</strong> for delivery <strong>{slotLabel}</strong>
      </span>
    </div>
  );
}
